import { NgModule, importProvidersFrom, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './pages/login/login.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PrivateHeaderComponent } from './components/private-header/private-header.component';
import { AuthInterceptor } from './services/interceptor/auth.interceptor';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NoTemplateListComponent } from './pages/no-template-list/no-template-list.component';
import { TemplateFormComponent } from './pages/template-form/template-form.component';
import { AnamneseAnswersComponent } from './pages/anamnese-answers/anamnese-answers.component';
import { QRCodeModule } from 'angularx-qrcode';
import { PublicHeaderComponent } from './components/public-header/public-header.component';
import { NgxPrintModule } from 'ngx-print';
import { LoadingComponent } from './components/loading/loading.component';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { ProcedureSelectModalComponent } from './layouts/main-v2/components/procedure-select-modal/procedure-select-modal.component';
import { NgxPhotoEditorModule } from 'ngx-photo-editor';
import { AnamneseChoosePopupComponent } from './components/anamnese-choose-popup/anamnese-choose-popup.component';
import { StoreModule } from '@ngrx/store';
import { anamneseChoosePopupModalReducer } from './reducers/anamnese-choose-popup.reducer';
import { LandingPageComponent } from './pages/landing-page/landing-page.component';
import { LandingPageHeaderComponent } from './components/landing-page-header/landing-page-header.component';
import { WarningModalComponent } from './components/warning-modal/warning-modal.component';
import { warningPopup } from './reducers/warning-popup.reducer';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BarChartComponent } from './components/charts/bar-chart/bar-chart.component';
import { GroupedBarChartComponent } from './components/charts/grouped-bar-chart/grouped-bar-chart.component';
import { PieChartComponent } from './components/charts/pie-chart/pie-chart.component';
import { RankingChartComponent } from './components/charts/ranking-chart/ranking-chart.component';
import { RevenueHistoryChartComponent } from './components/charts/revenue-history-chart/revenue-history-chart.component';
import { TicketHistoryChartComponent } from './components/charts/ticket-history-chart/ticket-history-chart.component';
import { PatientMapModule } from './components/patient-map/patient-map.module';
import { ClinicMapComponent } from './components/clinic-map/clinic-map.component';
import { DocumentUtilsService } from './services/document-utils.service';
import { PostalCodeService } from './services/postal-code.service';
import { TreatmentPlanComponent } from './pages/treatment-plan/treatment-plan.component';
import { UploadPhotosComponent } from './pages/treatment-plan/upload-product-photos/upload-photos.component';
import { UploadTreatmentPhotosComponent } from './pages/treatment-plan/upload-treatment-photos/upload-treatment-photos.component';
import { AnamneseInvalidComponent } from './pages/anamnese-invalid/anamnese-invalid.component';
import { AnamneseSuccessComponent } from './pages/anamnese-success/anamnese-success.component';
import { LinkInvalidComponent } from './pages/link-invalid/link-invalid.component';
import { AnamneseCustomQuestionsModalComponent } from './components/anamnese-custom-questions-modal/anamnese-custom-questions-modal.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AutomationServiceModalComponent } from './components/automation-services-modal/automation-services-modal.component';
import { MainV2Component } from './layouts/main-v2/main-v2.component';
import { HeaderComponent } from './layouts/main-v2/components/header/header.component';
import { SidebarComponent } from './layouts/main-v2/components/sidebar/sidebar.component';
import { ClinicSwitcherComponent } from './components/clinic-switcher/clinic-switcher.component';
import { CustomDropdownComponent } from './layouts/main-v2/components/custom-dropdown/custom-dropdown.component';
import { IconComponent } from './layouts/main-v2/icon/icon.component';
import { CompanyComponent } from './layouts/main-v2/pages/company/company.component';
import { CnpjPipe } from './layouts/utils/mask/cnpj.pipe';
import { CompanyFormComponentV2 } from './layouts/main-v2/pages/company-form/company-form.component';
import { StepperComponent } from './layouts/main-v2/components/stepper/stepper.component';
import { InfoComponent } from './layouts/main-v2/pages/company-form/steps/info/info.component';
import { AddressComponent } from './layouts/main-v2/pages/company-form/steps/address/address.component';
import { ContactComponent } from './layouts/main-v2/pages/company-form/steps/contact/contact.component';
import { SettingsComponent } from './layouts/main-v2/pages/company-form/steps/settings/settings.component';
import { ProvidersComponent } from './layouts/main-v2/pages/providers/providers.component';
import { ProviderFormComponent as ProviderFormComponentV2 } from './layouts/main-v2/pages/providers-form/provider-form.component';
import { ProviderEditComponent } from './layouts/main-v2/pages/providers-edit/provider-edit.component';
import { UsersV2Component } from './layouts/main-v2/pages/users/users.component';
import { UserFormV2Component } from './layouts/main-v2/pages/user-form/user-form.component';
import { UserEditV2Component } from './layouts/main-v2/pages/user-edit/user-edit.component';
import { ProceduresV2Component } from './layouts/main-v2/pages/procedures/procedures.component';
import { ProcedureFormV2Component } from './layouts/main-v2/pages/procedure-form/procedure-form.component';
import { ProcedureEditV2Component } from './layouts/main-v2/pages/procedure-edit/procedure-edit.component';
import { PatientsV2Component } from './layouts/main-v2/pages/patients/patients.component';
import { PatientFormV2Component } from './layouts/main-v2/pages/patient-form/patient-form.component';
import { PatientEditV2Component } from './layouts/main-v2/pages/patient-edit/patient-edit.component';
import { AnamneseViewV2Component } from './layouts/main-v2/pages/anamnese-view/anamnese-view.component';
import { AnamneseCreateV2Component } from './layouts/main-v2/pages/anamnese-create/anamnese-create.component';
import { GenerateDocsV2Component } from './layouts/main-v2/pages/generate-docs/generate-docs.component';
import { DocumentFormV2Component } from './layouts/main-v2/pages/document-form/document-form.component';
import { DocumentSuccessV2Component } from './layouts/main-v2/pages/document-success/document-success.component';
import { PendingAnamnesesV2Component } from './layouts/main-v2/pages/pending-anamneses/pending-anamneses.component';
import { PendingSignaturesV2Component } from './layouts/main-v2/pages/pending-signatures/pending-signatures.component';
import { TreatmentPlansV2Component } from './layouts/main-v2/pages/treatment-plans/treatment-plans.component';
import { TreatmentPlanDetailV2Component } from './layouts/main-v2/pages/treatment-plan-detail/treatment-plan-detail.component';
import { IncidentDetailV2Component } from './layouts/main-v2/pages/incident-detail/incident-detail.component';
import { DashboardV2Component } from './layouts/main-v2/pages/dashboard/dashboard.component';
import { TemplatesV2Component } from './layouts/main-v2/pages/templates/templates.component';
import { StandaloneDocsV2Component } from './layouts/main-v2/pages/standalone-docs/standalone-docs.component';
import { ClinicGroupsV2Component } from './layouts/main-v2/pages/clinic-groups/clinic-groups.component';
import { ClinicGroupFormV2Component } from './layouts/main-v2/pages/clinic-group-form/clinic-group-form.component';
import { ClinicGroupDetailComponent } from './layouts/main-v2/pages/clinic-group-detail/clinic-group-detail.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { VerifyCodeComponent } from './pages/verify-code/verify-code.component';
import { TemporaryPasswordModalComponent } from './components/temporary-password-modal/temporary-password-modal.component';
import { SessionConflictModalComponent } from './components/session-conflict-modal/session-conflict-modal.component';
import { IncidentFormComponent } from './components/incident-form/incident-form.component';
import { IncidentQuestionsComponent } from './layouts/main-v2/pages/incident-questions/incident-questions.component';
import { IncidentAttachmentComponent } from './components/incident-attachment/incident-attachment.component';
import { DragDropModule } from '@angular/cdk/drag-drop';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ForgotPasswordComponent,
    VerifyCodeComponent,
    PrivateHeaderComponent,
    NoTemplateListComponent,
    TemplateFormComponent,
    AnamneseAnswersComponent,
    PublicHeaderComponent,
    LoadingComponent,
    AnamneseChoosePopupComponent,
    LandingPageComponent,
    LandingPageHeaderComponent,
    WarningModalComponent,
    TreatmentPlanComponent,
    UploadPhotosComponent,
    UploadTreatmentPhotosComponent,
    AnamneseInvalidComponent,
    AnamneseSuccessComponent,
    LinkInvalidComponent,
    AnamneseCustomQuestionsModalComponent,
    AutomationServiceModalComponent,
    MainV2Component,
    HeaderComponent,
    CompanyComponent,
    CnpjPipe,
    InfoComponent,
    AddressComponent,
    ContactComponent,
    SettingsComponent,
    CompanyFormComponentV2,
    ProvidersComponent,
    ProviderFormComponentV2,
    ProviderEditComponent,
    UsersV2Component,
    UserFormV2Component,
    UserEditV2Component,
    ProceduresV2Component,
    ProcedureFormV2Component,
    ProcedureEditV2Component,
    PatientsV2Component,
    PatientFormV2Component,
    PatientEditV2Component,
    AnamneseViewV2Component,
    AnamneseCreateV2Component,
    GenerateDocsV2Component,
    DocumentFormV2Component,
    DocumentSuccessV2Component,
    PendingAnamnesesV2Component,
    PendingSignaturesV2Component,
    TreatmentPlansV2Component,
    TreatmentPlanDetailV2Component,
    IncidentDetailV2Component,
  DashboardV2Component,
  TemplatesV2Component,
  StandaloneDocsV2Component,
  ClinicGroupsV2Component,
  ClinicGroupFormV2Component,
  ClinicGroupDetailComponent,
  CustomDropdownComponent,
  TemporaryPasswordModalComponent,
  SessionConflictModalComponent,
  IncidentFormComponent,
  IncidentQuestionsComponent,
  IncidentAttachmentComponent,
  ],
  imports: [
    StepperComponent,
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DragDropModule,
    NgxMaskDirective,
    NgxMaskPipe,
    QRCodeModule,
    NgxPrintModule,
    NgxPhotoEditorModule,
    BrowserAnimationsModule,
    PatientMapModule,
    SidebarComponent,
    ClinicSwitcherComponent,
    IconComponent,
    StoreModule.forRoot({
      anamneseChoosePopupModal: anamneseChoosePopupModalReducer,
      warningPopup: warningPopup
    }, {}),
    BarChartComponent,
    GroupedBarChartComponent,
    PieChartComponent,
    RankingChartComponent,
    RevenueHistoryChartComponent,
    TicketHistoryChartComponent,
    ClinicMapComponent,
    LoadingSpinnerComponent,
    ProcedureSelectModalComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [importProvidersFrom(HttpClientModule), {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true
  },
  provideNgxMask(),
  DocumentUtilsService,
  PostalCodeService],
  bootstrap: [AppComponent]
})
export class AppModule { }
